import React, { useState, useRef } from 'react';
import { 
  doc, 
  updateDoc,
  getDoc
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { getAuth } from 'firebase/auth';
import { 
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  DocumentArrowUpIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const VendorDocumentCollection = ({ vendor, onClose, onSuccess }) => {
  const auth = getAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({});
  const [bypassDocuments, setBypassDocuments] = useState(false);
  
  // Document uploads
  const [documents, setDocuments] = useState({
    fssaiLicense: null,
    gstCertificate: null,
    panCard: null,
    aadharCard: null,
    bankDetails: null,
    shopEstablishment: null,
    menuPricing: null,
    kitchenPhotos: [],
    mouAgreement: null
  });

  // Document URLs after upload
  const [documentUrls, setDocumentUrls] = useState({});

  // File input refs
  const fileInputRefs = useRef({});

  // Required documents
  const requiredDocuments = [
    'fssaiLicense',
    'gstCertificate', 
    'panCard',
    'aadharCard',
    'bankDetails',
    'shopEstablishment',
    'menuPricing',
    'kitchenPhotos'
  ];

  // Check if all required documents are uploaded
  const isDocumentUploaded = (docName) => {
    if (docName === 'kitchenPhotos') {
      return documents[docName] && documents[docName].length > 0;
    }
    return documents[docName] !== null;
  };

  const areAllDocumentsUploaded = () => {
    return bypassDocuments || requiredDocuments.every(doc => isDocumentUploaded(doc));
  };

  // Handle file upload
  const handleFileUpload = async (docName, file) => {
    if (!file) return;

    setUploading(prev => ({ ...prev, [docName]: true }));

    try {
      // Create vendor ID from business name
      const vendorId = vendor.businessName?.toLowerCase().replace(/[^a-z0-9]/g, '_') || vendor.id;
      
      // Upload to Firebase Storage
      const storageRef = ref(storage, `MealDeskVendors/${vendorId}/Documents/${docName}_${Date.now()}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Update document URLs
      setDocumentUrls(prev => ({
        ...prev,
        [docName]: downloadURL
      }));

      // Update documents state
      if (docName === 'kitchenPhotos') {
        setDocuments(prev => ({
          ...prev,
          [docName]: [...(prev[docName] || []), file]
        }));
      } else {
        setDocuments(prev => ({
          ...prev,
          [docName]: file
        }));
      }

    } catch (error) {
      console.error(`Error uploading ${docName}:`, error);
      alert(`Error uploading ${docName}. Please try again.`);
    } finally {
      setUploading(prev => ({ ...prev, [docName]: false }));
    }
  };

  // Handle file input change
  const handleFileChange = (docName, event) => {
    const file = event.target.files[0];
    if (file) {
      handleFileUpload(docName, file);
    }
  };

  // Remove uploaded document
  const removeDocument = (docName) => {
    setDocuments(prev => ({
      ...prev,
      [docName]: docName === 'kitchenPhotos' ? [] : null
    }));
    setDocumentUrls(prev => {
      const newUrls = { ...prev };
      delete newUrls[docName];
      return newUrls;
    });
  };

  // Save documents and update vendor
  const saveDocuments = async () => {
    if (!areAllDocumentsUploaded()) {
      alert('Please upload all required documents or enable bypass option.');
      return;
    }

    setLoading(true);

    try {
      const currentUser = auth.currentUser;
      const vendorRef = doc(db, 'MealDeskVendors', vendor.id);

      // Get current vendor data
      const vendorDoc = await getDoc(vendorRef);
      if (!vendorDoc.exists()) {
        throw new Error('Vendor not found');
      }

      const currentData = vendorDoc.data();

      // Update vendor with documents and approval status
      const updateData = {
        onboardingDocs: {
          ...currentData.onboardingDocs,
          ...documentUrls
        },
        isApproved: true,
        bypassDocuments: bypassDocuments,
        approvedBy: currentUser?.uid || 'admin',
        approvedOn: new Date().toISOString(),
        status: 'approved',
        documentsCollectedAt: new Date().toISOString(),
        documentsCollectedBy: currentUser?.uid || 'admin'
      };

      await updateDoc(vendorRef, updateData);

      setLoading(false);
      onSuccess && onSuccess({ ...vendor, ...updateData });
      onClose && onClose();

    } catch (error) {
      console.error('Error saving documents:', error);
      alert('Error saving documents. Please try again.');
      setLoading(false);
    }
  };

  // Render document upload
  const renderDocumentUpload = (docName, label, required = false) => {
    const isUploaded = isDocumentUploaded(docName);
    const isUploading = uploading[docName];

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        
        {!isUploaded ? (
          <div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center hover:border-blue-400 transition-colors">
            <input
              type="file"
              ref={el => fileInputRefs.current[docName] = el}
              onChange={(e) => handleFileChange(docName, e)}
              accept="image/*,.pdf,.doc,.docx"
              className="hidden"
            />
            <button
              onClick={() => fileInputRefs.current[docName]?.click()}
              disabled={isUploading}
              className="flex items-center justify-center w-full space-x-2 text-gray-600 hover:text-blue-600"
            >
              {isUploading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              ) : (
                <DocumentArrowUpIcon className="h-5 w-5" />
              )}
              <span>{isUploading ? 'Uploading...' : 'Click to upload'}</span>
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center space-x-2">
              <CheckIcon className="h-5 w-5 text-green-600" />
              <span className="text-sm text-green-800">
                {docName === 'kitchenPhotos' 
                  ? `${documents[docName].length} photo(s) uploaded`
                  : 'Document uploaded'
                }
              </span>
            </div>
            <button
              onClick={() => removeDocument(docName)}
              className="text-red-600 hover:text-red-800"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
      
      <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
        <div className="w-screen max-w-2xl">
          <div className="h-full flex flex-col bg-white shadow-xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={onClose}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ArrowLeftIcon className="h-5 w-5" />
                  </button>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Collect Documents</h2>
                    <p className="text-sm text-gray-500">
                      Collect required documents for {vendor.name || vendor.businessName}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-6 py-4">
                {/* Vendor Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
                  <h3 className="text-sm font-medium text-blue-800 mb-2">Vendor Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-blue-700 font-medium">Name:</span>
                      <span className="ml-2 text-blue-600">{vendor.name || vendor.businessName}</span>
                    </div>
                    <div>
                      <span className="text-blue-700 font-medium">Contact:</span>
                      <span className="ml-2 text-blue-600">{vendor.contactName || vendor.contactPerson}</span>
                    </div>
                    <div>
                      <span className="text-blue-700 font-medium">Phone:</span>
                      <span className="ml-2 text-blue-600">{vendor.phone || vendor.contactPhone}</span>
                    </div>
                    <div>
                      <span className="text-blue-700 font-medium">Email:</span>
                      <span className="ml-2 text-blue-600">{vendor.contactEmail}</span>
                    </div>
                  </div>
                </div>

                {/* Document Upload Section */}
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Required Documents</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {renderDocumentUpload('fssaiLicense', 'FSSAI License', true)}
                    {renderDocumentUpload('gstCertificate', 'GST Certificate', true)}
                    {renderDocumentUpload('panCard', 'PAN Card', true)}
                    {renderDocumentUpload('aadharCard', 'Aadhar Card (Owner)', true)}
                    {renderDocumentUpload('bankDetails', 'Cancelled Cheque / Bank Details', true)}
                    {renderDocumentUpload('shopEstablishment', 'Shop & Establishment Certificate', true)}
                    {renderDocumentUpload('menuPricing', 'Menu & Pricing Document', true)}
                    {renderDocumentUpload('kitchenPhotos', 'Kitchen Photos', true)}
                    {renderDocumentUpload('mouAgreement', 'MoU / Agreement')}
                  </div>
                </div>

                {/* Bypass option */}
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                  <div className="flex items-center space-x-3">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
                    <div className="flex-1">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={bypassDocuments}
                          onChange={(e) => setBypassDocuments(e.target.checked)}
                          className="rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                        />
                        <span className="ml-2 text-sm font-medium text-yellow-800">
                          Bypass Document Requirements
                        </span>
                      </label>
                      <p className="text-xs text-yellow-700 mt-1">
                        Enable this to approve vendor without all required documents
                      </p>
                    </div>
                  </div>
                </div>

                {/* Progress Summary */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">Document Collection Progress</h4>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Required Documents</span>
                      <div className="flex items-center space-x-2">
                        {areAllDocumentsUploaded() ? (
                          <CheckIcon className="h-4 w-4 text-green-600" />
                        ) : (
                          <XMarkIcon className="h-4 w-4 text-red-600" />
                        )}
                        <span className={`text-xs ${areAllDocumentsUploaded() ? 'text-green-600' : 'text-red-600'}`}>
                          {bypassDocuments ? 'Bypassed' : `${requiredDocuments.filter(doc => isDocumentUploaded(doc)).length}/${requiredDocuments.length}`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                onClick={saveDocuments}
                disabled={loading || !areAllDocumentsUploaded()}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : 'Save Documents & Approve'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDocumentCollection; 